<template>
  <v-card class="pa-4 rounded-lg" elevation="1">
    <v-card-title class="text-h5 text-primary font-weight-bold pb-2">
      Thermostats
    </v-card-title>

    <v-card-subtitle class="text-body-2 text-primary pb-4">
      Select a thermostat to configure schedule settings.
    </v-card-subtitle>

    <v-divider class="mb-2" />

    <v-list v-if="thermostats.length" lines="two" class="rounded-lg">
      <v-list-item
        v-for="thermostat in thermostats"
        :key="thermostat.deviceId"
        :title="thermostat.friendlyName || `Thermostat ${thermostat.deviceId}`"
        :subtitle="
          thermostat.hasSchedule ? 'Schedule configured' : 'No schedule yet'
        "
        @click="goToScheduleSettings(thermostat)"
      >
        <template v-slot:append>
          <v-icon color="primary">mdi-chevron-right</v-icon>
        </template>
      </v-list-item>
    </v-list>

    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <BannerAlertVue
      v-if="!isLoading && !thermostats.length"
      message="No thermostats with configurable schedules at this site"
      class="mt-4"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import BannerAlertVue from '@/Core.UI.Domain/Components/BannerAlert.vue';
import API from '@/Core.Service.Domain/Controls/Thermostats/API';

// Shape of GET /v1/thermostats/sites/{siteId} — see DeviceWithScheduleResourceModel.
// These three fields are the whole contract; the endpoint returns no site name.
interface IThermostatListItem {
  deviceId?: number;
  friendlyName?: string;
  hasSchedule?: boolean;
}

const store = useStore();
const router = useRouter();

const thermostatRows = ref<IThermostatListItem[]>([]);
const isLoading = ref(false);

const defaultSite = computed<{ id: number } | null>(
  () => store.getters['session/defaultSite']
);

const thermostats = computed(() =>
  [...thermostatRows.value].sort((a, b) => {
    const aName = (a.friendlyName || '').toLowerCase();
    const bName = (b.friendlyName || '').toLowerCase();
    return aName.localeCompare(bName);
  })
);

async function loadThermostats() {
  isLoading.value = true;
  try {
    const siteId = defaultSite.value?.id;
    if (!siteId) {
      thermostatRows.value = [];
      return;
    }
    const rows = await API.getThermostatsBySite(siteId);
    thermostatRows.value = Array.isArray(rows) ? rows : [];
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
}

function goToScheduleSettings(thermostat: IThermostatListItem) {
  // Without an id the route resolves to /schedules/undefined and the settings
  // view fetches a schedule that cannot exist — stay put instead.
  if (!thermostat.deviceId) return;

  router.push({
    name: 'ThermostatScheduleSettings',
    params: { thermostatId: thermostat.deviceId },
  });
}

watch(defaultSite, () => loadThermostats(), { immediate: true });
</script>
